import React, { useEffect, useState } from "react";
import { Loader2, Save, CheckCircle2, AlertCircle, Mail } from "lucide-react";
import { adminApi } from "@/lib/api";

export default function SettingsPanel() {
  const [data, setData] = useState({
    recipient_email_1: "",
    recipient_email_2: "",
    recipient_email_3: "",
    sender_email: "",
    reply_to_email: "",
    email_signature: "",
    email_sending_enabled: true,
    updated_at: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get("/admin/settings");
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Σφάλμα φόρτωσης ρυθμίσεων");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k) => (e) => {
    setData({ ...data, [k]: e.target.value });
    setSuccess("");
  };

  async function save() {
    setError(""); setSuccess(""); setSaving(true);
    try {
      const r = await adminApi.put("/admin/settings", data);
      setData(r.data);
      setSuccess("Οι ρυθμίσεις αποθηκεύτηκαν επιτυχώς");
      setTimeout(() => setSuccess(""), 4000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Σφάλμα αποθήκευσης");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-blue-700" size={28} />
      </div>
    );
  }

  return (
    <div className="hf-fade-up max-w-3xl">
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Ρυθμίσεις</h1>
      <p className="text-slate-500 font-medium mt-2 mb-8">
        Αλλάξτε τις διευθύνσεις παραλήπτη και τα στοιχεία αποστολέα. Οι αλλαγές εφαρμόζονται αμέσως — δεν χρειάζεται redeploy.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 text-sm text-red-700 font-semibold flex items-center gap-2">
          <AlertCircle size={18} /> {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5 text-sm text-emerald-700 font-semibold flex items-center gap-2" data-testid="settings-success">
          <CheckCircle2 size={18} /> {success}
        </div>
      )}

      {/* Email sending toggle — first/most important setting */}
      <div className={`border-2 rounded-2xl p-6 md:p-7 mb-5 ${data.email_sending_enabled ? "bg-emerald-50/30 border-emerald-200" : "bg-amber-50 border-amber-300"}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-base font-extrabold tracking-tight text-slate-900 mb-1">
              {data.email_sending_enabled ? "📧 Αποστολή Email: Ενεργή" : "⏸️ Αποστολή Email: Απενεργοποιημένη"}
            </h3>
            <p className="text-sm text-slate-600 font-medium">
              {data.email_sending_enabled
                ? "Όταν ένας υποψήφιος πατήσει «Οριστική Υποβολή», στέλνεται email στους 3 παραλήπτες."
                : "Οι υποβολές αποθηκεύονται κανονικά στη βάση, αλλά ΔΕΝ στέλνεται email. Οι πελάτες βλέπουν μήνυμα επιτυχίας. Χρήσιμο όσο φτιάχνετε ρυθμίσεις παραλήπτη/domain."}
            </p>
          </div>
          <button
            type="button"
            data-testid="toggle-email-sending-btn"
            onClick={() => { setData({ ...data, email_sending_enabled: !data.email_sending_enabled }); setSuccess(""); }}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${data.email_sending_enabled ? "bg-emerald-600" : "bg-slate-400"}`}
            role="switch"
            aria-checked={data.email_sending_enabled}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${data.email_sending_enabled ? "translate-x-7" : "translate-x-1"}`}
            />
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 mb-5">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-2">
          <Mail size={14} /> Διευθύνσεις Παραλήπτη
        </h3>
        <p className="text-xs text-slate-400 font-medium mb-5">
          Οι 3 διευθύνσεις στις οποίες αποστέλλεται κάθε νέα φόρμα πρόσληψης (PDF + βιογραφικό).
        </p>
        <div className="space-y-4">
          {[1, 2, 3].map((n) => (
            <div key={n}>
              <label className="hf-label">Παραλήπτης {n}</label>
              <input
                type="email"
                className="hf-input"
                value={data[`recipient_email_${n}`] || ""}
                onChange={set(`recipient_email_${n}`)}
                placeholder={`recipient${n}@example.com`}
                data-testid={`recipient-email-${n}-input`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 mb-5">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-500 mb-1">Στοιχεία Αποστολέα</h3>
        <p className="text-xs text-slate-400 font-medium mb-5">
          Όνομα και διεύθυνση που εμφανίζεται στους παραλήπτες. Για να χρησιμοποιήσετε δικό σας domain (π.χ. <code>noreply@lhchellas.com</code>) πρέπει πρώτα να επαληθευτεί στο Resend.
        </p>
        <div className="space-y-4">
          <div>
            <label className="hf-label">Sender Email (From)</label>
            <input
              className="hf-input"
              value={data.sender_email || ""}
              onChange={set("sender_email")}
              placeholder="HireFlow <onboarding@resend.dev>"
              data-testid="sender-email-input"
            />
            <p className="text-xs text-slate-400 mt-2 font-medium">
              Μορφή: <code className="bg-slate-100 px-1.5 py-0.5 rounded">Όνομα &lt;email@domain.com&gt;</code>
            </p>
          </div>
          <div>
            <label className="hf-label">Reply-To Email</label>
            <input
              type="email"
              className="hf-input"
              value={data.reply_to_email || ""}
              onChange={set("reply_to_email")}
              placeholder="reply@example.com"
              data-testid="reply-to-email-input"
            />
            <p className="text-xs text-slate-400 mt-2 font-medium">
              Όταν ο παραλήπτης πατήσει «Reply», η απάντηση πάει σε αυτή τη διεύθυνση. <strong>Αφήστε κενό</strong> για να χρησιμοποιείται αυτόματα το email του υποψηφίου.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 mb-5">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-500 mb-1">Υπογραφή Email</h3>
        <p className="text-xs text-slate-400 font-medium mb-4">
          Προαιρετικό κείμενο που εμφανίζεται στο τέλος κάθε email πριν το footer. Π.χ. όνομα, θέση, εταιρεία, τηλέφωνο.
        </p>
        <textarea
          data-testid="email-signature-textarea"
          className="hf-input hf-textarea w-full"
          rows={6}
          value={data.email_signature || ""}
          onChange={set("email_signature")}
          placeholder={"π.χ.\nΧρήστος Χέλλας\nLH Hellas | Διαχείριση Προσωπικού\n📞 +30 210 1234567\n✉ chris@lhc.gr\n🌐 www.lhc.gr"}
        />
        <p className="text-xs text-slate-400 font-medium mt-2">
          Οι αλλαγές γραμμής εμφανίζονται κανονικά στο email. Δεν χρειάζεται HTML.
        </p>
      </div>

      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-slate-400 font-medium">
          {data.updated_at ? `Τελευταία ενημέρωση: ${data.updated_at.slice(0, 16).replace("T", " ")}` : "Δεν έχει ενημερωθεί ακόμα"}
        </p>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-settings-btn"
          className="h-12 px-6 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl shadow-[0_8px_20px_-8px_rgba(29,78,216,0.5)] active:scale-[0.98] transition-all flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          {saving ? "Αποθήκευση..." : "Αποθήκευση Ρυθμίσεων"}
        </button>
      </div>
    </div>
  );
}
